import type {
  AssessmentListFilters,
  CustomerListFilters,
  LoansListFilters,
} from './types';

/**
 * Query keys for customer-related queries
 * Used with TanStack Query for caching and invalidation
 */
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  /** One page of GET /customers; `filters` holds only the set ones. */
  list: (
    page: number,
    itemsPerPage: number,
    search: string = '',
    filters: CustomerListFilters = {}
  ) =>
    [
      ...customerKeys.lists(),
      { page, itemsPerPage, search, ...filters },
    ] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  loans: (customerId: string) =>
    [...customerKeys.detail(customerId), 'loans'] as const,
  loan: (customerId: string, loanId: string) =>
    [...customerKeys.loans(customerId), loanId] as const,
  logs: (customerId: string, page: number, itemsPerPage: number) =>
    [
      ...customerKeys.detail(customerId),
      'logs',
      { page, itemsPerPage },
    ] as const,
  logsInfinite: (customerId: string, itemsPerPage: number) =>
    [
      ...customerKeys.detail(customerId),
      'logs',
      'infinite',
      { itemsPerPage },
    ] as const,
  scheduledPayments: (loanId: string, repaymentsOnly: boolean = true) =>
    ['loans', loanId, 'scheduled-payments', { repaymentsOnly }] as const,
  loanLogs: (loanId: string, page: number, itemsPerPage: number) =>
    ['loans', loanId, 'logs', { page, itemsPerPage }] as const,
  loanLogsInfinite: (loanId: string, itemsPerPage: number) =>
    ['loans', loanId, 'logs', 'infinite', { itemsPerPage }] as const,
  /** Prefix of both loan-log keys above — for invalidation. */
  loanLogsAll: (loanId: string) => ['loans', loanId, 'logs'] as const,
  assessments: (customerId: string) =>
    [...customerKeys.detail(customerId), 'assessments'] as const,
  /**
   * Live payment methods on file (GET /customers/{id}/payment-methods). Under
   * detail() so the Update Bank Account invalidation refreshes it unchanged.
   */
  paymentMethods: (customerId: string) =>
    [...customerKeys.detail(customerId), 'payment-methods'] as const,
  /** Under detail() so a customer-wide invalidation refreshes it too. */
  employmentInfo: (customerId: string) =>
    [...customerKeys.detail(customerId), 'employment-info'] as const,
  /** Successful Referrals this customer made (GET /customers/{id}/referrals). */
  referrals: (customerId: string) =>
    [...customerKeys.detail(customerId), 'referrals'] as const,
  /** Marketing Status toggle (GET /customers/{id}/marketing-preferences). */
  marketingStatus: (customerId: string) =>
    [...customerKeys.detail(customerId), 'marketing-status'] as const,
  /** Prefix of every assessmentDetail key — for cross-customer invalidation. */
  assessmentDetails: () => ['assessments'] as const,
  assessmentDetail: (assessmentId: string) =>
    [...customerKeys.assessmentDetails(), assessmentId] as const,
  assessmentBankStatements: (assessmentId: string) =>
    [
      ...customerKeys.assessmentDetail(assessmentId),
      'bank-statements',
    ] as const,
  /**
   * One page of GET /assessments (every customer); `filters` holds only the
   * set ones. Under assessmentDetails() so an assessment-wide invalidation
   * (a PATCH from the client tab) refreshes the list too.
   */
  allAssessmentsList: (
    page: number,
    itemsPerPage: number,
    filters: AssessmentListFilters = {}
  ) =>
    [
      ...customerKeys.assessmentDetails(),
      'list',
      { page, itemsPerPage, ...filters },
    ] as const,
  ledger: (customerId: string, loanId: string) =>
    [...customerKeys.loan(customerId, loanId), 'ledger'] as const,
  offers: (customerId: string, loanId: string) =>
    [...customerKeys.loan(customerId, loanId), 'offers'] as const,
  allLoans: () => ['loans'] as const,
  /** One page of GET /loans; `filters` holds only a bounded window. */
  allLoansList: (
    page: number,
    itemsPerPage: number,
    filters: LoansListFilters = {}
  ) =>
    [
      ...customerKeys.allLoans(),
      'list',
      { page, itemsPerPage, ...filters },
    ] as const,
};
